"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
var api_external_contract_js_1 = require("@tableau/api-external-contract-js");
var TableauError_1 = require("../TableauError");
/**
 * Helper methods for Export APIs.
 *
 * @class ExportHelpers
 */
var ExportHelpers = /** @class */ (function () {
    function ExportHelpers() {
    }
    /**
     * Modifies the internal ExportPDFOptions' properties based on the given PDF export configuration.
     * This method will construct the internal ExportPDFOptions identically to how the 'Export PDF' dialog constructs the ExportPdfOptionsPresModel.
     * See the handleExportClick method in @browser-clients/export-pdf-options-dialog for reference.
     *
     * @param internalExportPdfOptions The internal ExportPDFOptions pres model used to configure the output PDF file.
     * @param externalExportPdfOptions The external ExportPDFOptions user-facing object passed to exportPDFAsync.
     * @param sheetNames The list of sheets selected for export.
     */
    ExportHelpers.updateInternalExportPDFOptions = function (internalExportPdfOptions, externalExportPdfOptions, sheetNames) {
        internalExportPdfOptions.currentSheet = sheetNames[0];
        this.updateExportLayoutOptions(internalExportPdfOptions, externalExportPdfOptions);
        this.updateSheetOptions(internalExportPdfOptions, sheetNames);
    };
    /**
     * Helper method for updateInternalExportPDFOptions.
     * Modifies the exportLayoutOptions property of the internal ExportPDFOptions based on the external ExportPDFOptions.
     * See the getExportLayoutOptions method in @browser-clients/export-pdf-options-dialog for reference.
     *
     * @param internalExportPdfOptions The internal ExportPDFOptions pres model used to configure the output PDF file.
     * @param externalExportPdfOptions The external ExportPDFOptions user-facing object passed to exportPDFAsync.
     */
    ExportHelpers.updateExportLayoutOptions = function (internalExportPdfOptions, externalExportPdfOptions) {
        var exportLayoutOptions = internalExportPdfOptions.exportLayoutOptions;
        // setting the image height and width properties to their default values
        exportLayoutOptions.imageHeight = this.DefaultLayoutOptions.imageHeight;
        exportLayoutOptions.imageWidth = this.DefaultLayoutOptions.imageWidth;
        //  setting the orientation value for the exportLayoutOptions
        switch (externalExportPdfOptions.orientation) {
            case api_external_contract_js_1.PrintOrientation.Landscape:
                exportLayoutOptions.pageOrientationOption = api_internal_contract_js_1.PageOrientation.Landscape;
                break;
            case api_external_contract_js_1.PrintOrientation.Portrait:
                exportLayoutOptions.pageOrientationOption = api_internal_contract_js_1.PageOrientation.Portrait;
                break;
            default:
                throw new TableauError_1.TableauError(api_external_contract_js_1.SharedErrorCodes.InternalError, 'invalid orientation for ExportPDFOption');
        }
        // setting the page size value for the exportLayoutOptions
        switch (externalExportPdfOptions.pageSize) {
            case api_external_contract_js_1.PrintPageSize.A3:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.A3;
                break;
            case api_external_contract_js_1.PrintPageSize.A4:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.A4;
                break;
            case api_external_contract_js_1.PrintPageSize.A5:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.A5;
                break;
            case api_external_contract_js_1.PrintPageSize.B4:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.B4;
                break;
            case api_external_contract_js_1.PrintPageSize.B5:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.B5;
                break;
            case api_external_contract_js_1.PrintPageSize.Executive:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Executive;
                break;
            case api_external_contract_js_1.PrintPageSize.Folio:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Folio;
                break;
            case api_external_contract_js_1.PrintPageSize.Ledger:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Ledger;
                break;
            case api_external_contract_js_1.PrintPageSize.Legal:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Legal;
                break;
            case api_external_contract_js_1.PrintPageSize.Letter:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Letter;
                break;
            case api_external_contract_js_1.PrintPageSize.Note:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Note;
                break;
            case api_external_contract_js_1.PrintPageSize.Quarto:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Quarto;
                break;
            case api_external_contract_js_1.PrintPageSize.Statement:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Statement;
                break;
            case api_external_contract_js_1.PrintPageSize.Tabloid:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Tabloid;
                break;
            case api_external_contract_js_1.PrintPageSize.Unspecified:
                exportLayoutOptions.pageSizeOption = api_internal_contract_js_1.PageSizeOption.Unspecified;
                break;
            default:
                throw new TableauError_1.TableauError(api_external_contract_js_1.SharedErrorCodes.InternalError, 'invalid pageSize for ExportPDFOption');
        }
        // setting the scaling values for the exportLayoutOptions
        switch (externalExportPdfOptions.scaling) {
            case api_external_contract_js_1.PrintScaling.AtMost1PageHigh:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.FitPages.FitOneHigh);
                break;
            case api_external_contract_js_1.PrintScaling.AtMost1PageWide:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.FitPages.FitOneWide);
                break;
            case api_external_contract_js_1.PrintScaling.AtMost2PagesHigh:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.FitPages.FitTwoHigh);
                break;
            case api_external_contract_js_1.PrintScaling.AtMost2PagesWide:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.FitPages.FitTwoWide);
                break;
            case api_external_contract_js_1.PrintScaling.Automatic:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePctAuto);
                break;
            case api_external_contract_js_1.PrintScaling.Perc100:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct100);
                break;
            case api_external_contract_js_1.PrintScaling.Perc200:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct200);
                break;
            case api_external_contract_js_1.PrintScaling.Perc25:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct25);
                break;
            case api_external_contract_js_1.PrintScaling.Perc400:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct400);
                break;
            case api_external_contract_js_1.PrintScaling.Perc50:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct50);
                break;
            case api_external_contract_js_1.PrintScaling.Perc60:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct60);
                break;
            case api_external_contract_js_1.PrintScaling.Perc75:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct75);
                break;
            case api_external_contract_js_1.PrintScaling.Perc80:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct80);
                break;
            case api_external_contract_js_1.PrintScaling.Perc90:
                this.setFitAndScaleValues(exportLayoutOptions, api_internal_contract_js_1.PageScalingOption.ScalePct90);
                break;
            default:
                throw new TableauError_1.TableauError(api_external_contract_js_1.SharedErrorCodes.InternalError, 'invalid scaling for ExportPDFOption');
        }
    };
    /**
     * Helper method for updateInternalExportPDFOptions.
     * Modifies the sheetOptions property of the internal ExportPDFOptions based on the sheets selected for export.
     * See the handleExportClick method in @browser-clients/export-pdf-options-dialog for reference.
     *
     * @param internalExportPdfOptions The internal ExportPDFOptions pres model used to configure the output PDF file.
     * @param sheetNames The list of sheets selected for export.
     */
    ExportHelpers.updateSheetOptions = function (internalExportPdfOptions, sheetNames) {
        var exportLayoutOptions = internalExportPdfOptions.exportLayoutOptions, sheetOptions = internalExportPdfOptions.sheetOptions;
        // updating the export layouts options for each sheet and selecting sheet for export if included in list
        for (var _i = 0, sheetOptions_1 = sheetOptions; _i < sheetOptions_1.length; _i++) {
            var sheetOption = sheetOptions_1[_i];
            sheetOption.exportLayoutOptions = exportLayoutOptions;
            sheetOption.isSelected = sheetNames.includes(sheetOption.sheet);
        }
    };
    /**
     * Helper method for updateExportLayoutOptons.
     * This method is a spoof of the getFitAndScaleValues method used in @browser-clients/export-pdf-options to set the exportLayoutOptions' scaling properties.
     * In the future, this file should be refactored to import the method directly from @browser-clients/export-pdf-options.
     *
     * @param scalingSelection string used for setting the exportLayoutOptions based on the PrintScaling property from the ExternalExportPDFOptions.
     * @param exportLayoutOptions the ExportLayoutOptions used by the internal ExportPDFOptions pres model.
     */
    ExportHelpers.setFitAndScaleValues = function (exportLayoutOptions, scalingSelection) {
        var _a;
        var _b = this.DefaultLayoutOptions, pageFitHorizontal = _b.pageFitHorizontal, pageFitVertical = _b.pageFitVertical, pageScaleMode = _b.pageScaleMode, pageScalePercent = _b.pageScalePercent;
        var fitMatch = this.fitRegex.exec(scalingSelection);
        var percentMatch = this.pctRegex.exec(scalingSelection);
        if (fitMatch) {
            _a = fitMatch.slice(1).map(function (value) { return Number(value); }), pageFitHorizontal = _a[0], pageFitVertical = _a[1];
            pageScaleMode = api_internal_contract_js_1.ScaleMode.ScaleFitPages;
        }
        else if (percentMatch) {
            pageScalePercent = percentMatch.slice(1).map(function (value) { return Number(value); })[0];
            pageScaleMode = api_internal_contract_js_1.ScaleMode.ScalePercentage;
        }
        // getFitAndScaleValues returns the properties below, but instead this method will set them.
        exportLayoutOptions.pageFitHorizontal = pageFitHorizontal;
        exportLayoutOptions.pageFitVertical = pageFitVertical;
        exportLayoutOptions.pageScaleMode = pageScaleMode;
        exportLayoutOptions.pageScalePercent = pageScalePercent;
    };
    ExportHelpers.DefaultDataOptions = {
        ignoreAliases: false,
        columnsToIncludeById: [],
    };
    ExportHelpers.DefaultPDFOptions = {
        scaling: api_external_contract_js_1.PrintScaling.Automatic,
        pageSize: api_external_contract_js_1.PrintPageSize.Letter,
        orientation: api_external_contract_js_1.PrintOrientation.Portrait,
    };
    ExportHelpers.DefaultLayoutOptions = {
        imageHeight: 0,
        imageWidth: 0,
        pageFitHorizontal: 1,
        pageFitVertical: 1,
        pageOrientationOption: api_internal_contract_js_1.PageOrientation.Portrait,
        pageScaleMode: api_internal_contract_js_1.ScaleMode.ScaleAuto,
        pageScalePercent: 100,
        pageSizeOption: api_internal_contract_js_1.PageSizeOption.Letter,
    };
    ExportHelpers.fitRegex = new RegExp('fit([0-9]+)x([0-9]+)');
    ExportHelpers.pctRegex = new RegExp('pct([0-9]+)');
    return ExportHelpers;
}());
exports.ExportHelpers = ExportHelpers;
//# sourceMappingURL=ExportHelpers.js.map